import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, STATUSES, URGENCY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Lock, MessageSquare, Handshake } from "lucide-react";
import { urgencyBadge, statusBadge } from "./SupportRequests";
import { toast } from "sonner";

export default function SupportDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isMgr = ["hr_leadership", "admin"].includes(user?.role);
    const [c, setC] = useState(null);
    const [note, setNote] = useState("");
    const [med, setMed] = useState({ title: "", parties: "", mediator: "", scheduled_at: "", notes: "" });
    const [medOpen, setMedOpen] = useState(false);
    const setM = (k, v) => setMed((f) => ({ ...f, [k]: v }));

    const load = () => api.get(`/support-requests/${id}`).then((r) => setC(r.data)).catch(() => toast.error("Case not found"));
    useEffect(() => { load(); }, [id]);

    const update = async (patch) => {
        try { const { data } = await api.patch(`/support-requests/${id}`, patch); setC(data); toast.success("Case updated"); }
        catch { toast.error("Update failed"); }
    };
    const addNote = async () => {
        if (!note.trim()) return;
        try { await api.post(`/support-requests/${id}/notes`, { body: note }); setNote(""); toast.success("Note added"); load(); }
        catch { toast.error("Could not add note"); }
    };
    const bookMediation = async () => {
        if (!med.title || !med.mediator || !med.scheduled_at) { toast.error("Title, mediator and date required"); return; }
        try { await api.post("/mediations", { ...med, request_id: id }); toast.success("Mediation booked"); setMedOpen(false); load(); }
        catch { toast.error("Could not book mediation"); }
    };

    if (!c) return <div className="text-slate-500">Loading case…</div>;

    return (
        <div className="space-y-6 max-w-4xl" data-testid="support-detail-page">
            <button onClick={() => navigate("/app/support")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-nhs-blue" data-testid="back-to-cases"><ArrowLeft className="h-4 w-4" /> Back to cases</button>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-nhs-ink">{c.subject}</h1>
                        <p className="text-slate-500 mt-1">{c.category}</p>
                    </div>
                    <div className="flex gap-2">
                        <Badge className={`${urgencyBadge(c.urgency)} border-0 capitalize`}>{c.urgency}</Badge>
                        <Badge className={`${statusBadge(c.status)} border-0`}>{c.status.replace("_", " ")}</Badge>
                    </div>
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-wrap">{c.description}</p>
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-500">
                    <span>Raised by: <strong className="text-nhs-ink">{c.requester_name}</strong></span>
                    <span>Org: {c.requester_org || "—"}</span>
                    {c.confidential && <span className="flex items-center gap-1 text-nhs-teal"><Lock className="h-3.5 w-3.5" /> Confidential</span>}
                </div>
            </div>

            {isMgr && (
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="triage-panel">
                    <h3 className="font-heading font-semibold text-lg text-nhs-ink mb-4">Triage & manage</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Status</Label>
                            <Select value={c.status} onValueChange={(v) => update({ status: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="triage-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Urgency</Label>
                            <Select value={c.urgency} onValueChange={(v) => update({ urgency: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="triage-urgency-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{URGENCY.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <Dialog open={medOpen} onOpenChange={setMedOpen}>
                        <DialogTrigger asChild>
                            <Button className="mt-4 bg-nhs-teal hover:bg-[#00857c] text-white gap-2" data-testid="book-mediation-button"><Handshake className="h-4 w-4" /> Book mediation</Button>
                        </DialogTrigger>
                        <DialogContent data-testid="mediation-dialog">
                            <DialogHeader><DialogTitle className="font-heading">Book early mediation</DialogTitle></DialogHeader>
                            <div className="space-y-3 py-2">
                                <div><Label>Title</Label><Input value={med.title} onChange={(e) => setM("title", e.target.value)} className="mt-1.5" data-testid="med-title" /></div>
                                <div><Label>Parties</Label><Input value={med.parties} onChange={(e) => setM("parties", e.target.value)} placeholder="e.g. staff member & line manager" className="mt-1.5" data-testid="med-parties" /></div>
                                <div><Label>Mediator</Label><Input value={med.mediator} onChange={(e) => setM("mediator", e.target.value)} className="mt-1.5" data-testid="med-mediator" /></div>
                                <div><Label>Date & time</Label><Input type="datetime-local" value={med.scheduled_at} onChange={(e) => setM("scheduled_at", e.target.value)} className="mt-1.5" data-testid="med-date" /></div>
                            </div>
                            <DialogFooter><Button onClick={bookMediation} className="bg-nhs-teal hover:bg-[#00857c] text-white" data-testid="confirm-mediation">Confirm booking</Button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            )}

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="case-notes">
                <h3 className="font-heading font-semibold text-lg text-nhs-ink mb-4 flex items-center gap-2"><MessageSquare className="h-5 w-5 text-nhs-blue" /> Case documentation</h3>
                {(!c.notes || c.notes.length === 0) ? <p className="text-sm text-slate-400">No case notes recorded yet.</p> : (
                    <ol className="space-y-4 border-l-2 border-slate-100 pl-5">
                        {c.notes.map((n) => (
                            <li key={n.id} className="relative" data-testid="case-note">
                                <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full bg-nhs-blue" />
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.body}</p>
                                <p className="text-xs text-slate-400 mt-1">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                            </li>
                        ))}
                    </ol>
                )}
                {isMgr && (
                    <div className="mt-4">
                        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a case note…" data-testid="note-input" />
                        <Button onClick={addNote} className="mt-2 bg-nhs-blue hover:bg-nhs-darkblue text-white" data-testid="add-note-button">Add note</Button>
                    </div>
                )}
            </div>
        </div>
    );
}
